import React, { useMemo } from 'react';
import { GridCellParams, InputTable, RepeatRowsPayload } from '@/components/custom/erp';
import { useInvoiceHydration } from '@/hooks/queries/useInvoiceHydration';
import InputTableActionBar from '../../InputTableActionBar';
import { SelectedCellInfo } from '../hooks/useDetailRows';
import { useDetailPaste } from '../hooks/useDetailPaste';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailColumns } from './columns';
import { FormMode } from '@/types/form';

interface CurrencyInfo {
  ma_nt: string;
  uuid: string;
}

interface DetailTabProps {
  mode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: any) => void;
  onCellClick?: (params: GridCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onExport: () => void;
  onPin: () => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: any) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<any>) => void;
  currencyInfo?: CurrencyInfo | null;
  exchangeRate?: number;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<any[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

const DetailTab: React.FC<DetailTabProps> = ({
  mode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onExport,
  onPin,
  onCellValueChange,
  onRepeatForRows,
  currencyInfo,
  exchangeRate,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  useInvoiceHydration({
    rows,
    enabled: mode !== 'add',
    referenceField: 'id_tt',
    dataField: 'id_tt_data',
    onHydrated: (uuid, invoice) => onCellValueChange(uuid, 'id_tt_data', invoice)
  });

  const columns = useMemo(
    () => getDetailColumns(mode, onCellValueChange, currencyInfo, exchangeRate),
    [mode, onCellValueChange, currencyInfo, exchangeRate]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu={true}
      onCopyData={({ columnField, columnValue, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => console.log('Copy data per column:', text)
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <InputTableActionBar
          mode={mode}
          selectedCell={selectedCell}
          handleAddRow={onAddRow}
          handleDeleteRow={onDeleteRow}
          handleCopyRow={onCopyRow}
          handlePasteRow={onPasteRow}
          handleMoveRow={onMoveRow}
          handleExport={onExport}
          handlePin={onPin}
          isPasting={isPasting}
        />
      }
    />
  );
};

export default DetailTab;
